import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ImageUp, Loader2 } from 'lucide-react';
import { type DragEvent, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { EmptyState } from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth';
import { listMedia, type MediaAsset, uploadMedia } from '@/lib/media';
import { cn } from '@/lib/utils';

/**
 * The media library.
 *
 * Everything uploaded anywhere in the console lands here -- product photos,
 * inline images from the editors, and files dropped on this page -- because
 * every upload path records a `media_assets` row. The grid is therefore the
 * one place an image can be found again, which is the whole point of a
 * library and the reason ctrl-saas's write-only uploads were a dead end.
 */
export const Route = createFileRoute('/media/')({
  component: MediaPage,
});

export function formatBytes(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;

  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function MediaPage() {
  const { activeAccountId, can } = useAuth();
  const accountId = activeAccountId ?? '';
  const queryClient = useQueryClient();

  const canManage = can('cms.manage');

  const inputRef = useRef<HTMLInputElement>(null);
  const [dropActive, setDropActive] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [search, setSearch] = useState('');
  const [tag, setTag] = useState<string | null>(null);

  const assets = useQuery({
    queryKey: ['media', accountId],
    queryFn: () => listMedia(accountId),
    enabled: Boolean(accountId),
  });

  const tags = useMemo(() => {
    const seen = new Set<string>();

    for (const asset of assets.data ?? []) {
      for (const value of asset.tags) seen.add(value);
    }

    return [...seen].sort();
  }, [assets.data]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return (assets.data ?? []).filter((asset) => {
      if (tag && !asset.tags.includes(tag)) return false;
      if (!needle) return true;

      return (
        asset.filename.toLowerCase().includes(needle) ||
        (asset.alt ?? '').toLowerCase().includes(needle) ||
        asset.tags.some((value) => value.toLowerCase().includes(needle))
      );
    });
  }, [assets.data, search, tag]);

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const images = files.filter((file) => file.type.startsWith('image/'));

      if (!images.length) throw new Error('Only image files can be uploaded.');

      setUploading(images.length);

      let uploaded = 0;

      // Sequential: these are multi-megabyte files going through a Worker,
      // and a batch that fails halfway should say which ones landed.
      for (const file of images) {
        try {
          await uploadMedia(accountId, file, 'content');
          uploaded += 1;
        } catch (error) {
          toast.error(`${file.name}: ${(error as Error).message}`);
        } finally {
          setUploading((count) => count - 1);
        }
      }

      return uploaded;
    },
    onSuccess: (uploaded) => {
      setUploading(0);
      if (inputRef.current) inputRef.current.value = '';
      if (uploaded) toast.success(`${uploaded} ${uploaded === 1 ? 'file' : 'files'} uploaded.`);
      queryClient.invalidateQueries({ queryKey: ['media', accountId] });
    },
    onError: (error: Error) => {
      setUploading(0);
      toast.error(error.message);
    },
  });

  function handleDrop(event: DragEvent) {
    event.preventDefault();
    setDropActive(false);

    if (!canManage) return;

    upload.mutate(Array.from(event.dataTransfer?.files ?? []));
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Media library</h1>
          <p className="text-muted-foreground text-sm">
            {assets.isLoading
              ? 'Loading…'
              : `${assets.data?.length ?? 0} files · showing ${visible.length}`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Input
            className="w-56"
            placeholder="Search files and tags"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {canManage && (
            <Button type="button" onClick={() => inputRef.current?.click()}>
              <ImageUp className="size-4" />
              Upload
            </Button>
          )}
        </div>
      </div>

      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map((value) => (
            <button key={value} type="button" onClick={() => setTag(tag === value ? null : value)}>
              <Badge variant={tag === value ? 'default' : 'secondary'}>{value}</Badge>
            </button>
          ))}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(event) => upload.mutate(Array.from(event.target.files ?? []))}
      />

      {/* biome-ignore lint/a11y/noStaticElementInteractions: the drop target is a convenience; the Upload button is the keyboard path */}
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDropActive(true);
        }}
        onDragLeave={() => setDropActive(false)}
        onDrop={handleDrop}
        className={cn(
          'rounded-lg border border-dashed p-4 transition-colors',
          dropActive ? 'border-primary bg-primary/5' : 'border-transparent',
        )}
      >
        {assets.isLoading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {[0, 1, 2, 3, 4].map((key) => (
              <Skeleton key={key} className="aspect-square w-full" />
            ))}
          </div>
        ) : visible.length ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {uploading > 0 && (
              <div className="text-muted-foreground flex aspect-square items-center justify-center rounded-lg border">
                <Loader2 className="size-5 animate-spin" />
              </div>
            )}
            {visible.map((asset) => (
              <AssetTile key={asset.id} asset={asset} />
            ))}
          </div>
        ) : (
          <EmptyState>
            <ImageUp className="mx-auto mb-2 size-6" />
            {assets.data?.length
              ? 'Nothing matches that search.'
              : 'Nothing here yet — drop images anywhere on this panel.'}
          </EmptyState>
        )}
      </div>
    </div>
  );
}

function AssetTile({ asset }: { asset: MediaAsset }) {
  return (
    <Link
      to="/media/$assetId"
      params={{ assetId: asset.id }}
      className="group focus-visible:ring-ring block rounded-lg focus-visible:ring-2 focus-visible:outline-none"
    >
      <div className="bg-muted aspect-square overflow-hidden rounded-lg border">
        {asset.publicUrl ? (
          <img
            src={asset.publicUrl}
            alt={asset.alt ?? asset.filename}
            loading="lazy"
            className="size-full object-cover transition-transform group-hover:scale-105"
          />
        ) : null}
      </div>
      <p className="mt-2 truncate text-sm font-medium">{asset.filename}</p>
      <p className="text-muted-foreground truncate text-xs">
        {asset.width && asset.height ? `${asset.width}×${asset.height} · ` : ''}
        {formatBytes(asset.sizeBytes)}
      </p>
    </Link>
  );
}
